import {
  useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import { subtrackFacet } from '../logic/dashboard';
import {
  SUBTRACKS, SUBTRACK_GROUP_LABELS, SUBTRACK_NONE, type SubtrackGroup,
} from '../logic/subtracks';

interface SubtrackFilterProps {
  filters?: unknown[];
  selected?: string[];
  onChange: (next: string[]) => void;
}

const GROUPS: SubtrackGroup[] = ['eckm', 'ckm'];

function GroupBox({
  id, checked, partial, onToggle, label, count,
}: {
  id: string; checked: boolean; partial: boolean; onToggle: () => void; label: string; count: number;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // Layout, not passive: the dash is set before the menu paints.
  useLayoutEffect(() => { if (ref.current) ref.current.indeterminate = partial; }, [partial]);
  return (
    <label htmlFor={id} className={`filter-option${checked ? ' is-checked' : ''}`}>
      <input id={id} ref={ref} type="checkbox" checked={checked} onChange={onToggle} className="subtrack-filter__box" />
      <strong className="subtrack-filter__label">{label}</strong>
      <span className="subtrack-filter__count">{count}</span>
    </label>
  );
}

// The toolbar's sub-track menu, drawn only when apiv2 sends the subtrack facet (roster opt-in).
// It offers only the codes the app knows: those are the only ones the URL check lets through.
export default function SubtrackFilter({ filters = [], selected = [], onChange }: SubtrackFilterProps) {
  const facet = subtrackFacet(filters);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    const outside = (e: Event) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      chipRef.current?.focus();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('focusin', outside);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('focusin', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!facet) return null;

  const countOf = (code: string) => facet.options.find((o) => o.value === code)?.count ?? 0;
  const groupCount = (g: SubtrackGroup) => facet.groups.find((x) => x.value === g)?.count
    ?? SUBTRACKS.filter((s) => s.group === g).reduce((n, s) => n + countOf(s.code), 0);
  const toggle = (code: string) => onChange(selected.includes(code) ? selected.filter((v) => v !== code) : [...selected, code]);
  const toggleGroup = (g: SubtrackGroup) => {
    const codes = SUBTRACKS.filter((s) => s.group === g).map((s) => s.code);
    const all = codes.every((c) => selected.includes(c));
    onChange(all ? selected.filter((v) => !codes.includes(v)) : [...selected, ...codes.filter((c) => !selected.includes(c))]);
  };

  const q = query.trim().toLowerCase();
  const matches = (code: string, label: string) => !q || code.toLowerCase().includes(q) || label.toLowerCase().includes(q);
  const visible = (code: string) => showAll || countOf(code) > 0 || selected.includes(code);

  const n = selected.length;
  let chipLabel = 'Sub-track';
  if (n === 1) chipLabel = selected[0] === SUBTRACK_NONE ? 'Sub-track: none yet' : `Sub-track: ${selected[0]}`;
  else if (n > 1) chipLabel = `Sub-track · ${n}`;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        ref={chipRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={`filter-chip${n > 0 ? ' is-selected' : ''}`}
      >
        <span>{chipLabel}</span>
        <span className="filter-chip__caret">▾</span>
      </button>
      {open && (
        <div id={menuId} role="dialog" aria-label="Filter by sub-track" className="subtrack-filter">
          <div className="subtrack-filter__head">
            <span className="eyebrow text-secondary">Sub-track</span>
            {n > 0 && <button type="button" className="filter-clear" onClick={() => onChange([])}>Clear</button>}
          </div>
          <input
            ref={searchRef}
            type="search"
            className="subtrack-filter__search"
            placeholder="Search a code or condition"
            aria-label="Search sub-tracks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {GROUPS.map((g) => {
            const codes = SUBTRACKS.filter((s) => s.group === g);
            const rows = codes.filter((s) => visible(s.code) && matches(s.code, s.label));
            const picked = codes.filter((s) => selected.includes(s.code)).length;
            return (
              <div key={g} role="group" aria-label={SUBTRACK_GROUP_LABELS[g]}>
                <GroupBox
                  id={`${menuId}-${g}`}
                  label={SUBTRACK_GROUP_LABELS[g]}
                  count={groupCount(g)}
                  checked={picked === codes.length}
                  partial={picked > 0 && picked < codes.length}
                  onToggle={() => toggleGroup(g)}
                />
                {rows.map((s) => (
                  <label key={s.code} htmlFor={`${menuId}-${s.code}`} className={`filter-option subtrack-filter__row${selected.includes(s.code) ? ' is-checked' : ''}`}>
                    <input id={`${menuId}-${s.code}`} type="checkbox" className="subtrack-filter__box" checked={selected.includes(s.code)} onChange={() => toggle(s.code)} />
                    <span className="subtrack-filter__code">{s.code}</span>
                    <span className="subtrack-filter__label">{s.label}</span>
                    <span className="subtrack-filter__count">{countOf(s.code)}</span>
                  </label>
                ))}
              </div>
            );
          })}
          {matches(SUBTRACK_NONE, 'no sub-track yet') && (
            <label htmlFor={`${menuId}-none`} className={`filter-option${selected.includes(SUBTRACK_NONE) ? ' is-checked' : ''}`}>
              <input id={`${menuId}-none`} type="checkbox" className="subtrack-filter__box" checked={selected.includes(SUBTRACK_NONE)} onChange={() => toggle(SUBTRACK_NONE)} />
              <span className="subtrack-filter__label">No sub-track yet</span>
              <span className="subtrack-filter__count">{countOf(SUBTRACK_NONE)}</span>
            </label>
          )}
          <div className="subtrack-filter__foot">
            <button type="button" className="filter-clear" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Hide sub-tracks with no patient' : `Show all ${SUBTRACKS.length} sub-tracks`}
            </button>
            <button type="button" className="filter-clear" onClick={() => { setOpen(false); chipRef.current?.focus(); }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
